import { UserPermissions } from '@creditsights/api-ui-web';
import { DataViewConfig, TableViewConfig } from '@creditsights/styleguide-web';
import { InitialActionAllower, RouteToCallback } from '@creditsights/styleguide-web/components/DataView';
import { MouseEvent } from 'react';
import { ROUTES } from '../../../Base.constants';
import {
  ArticleTitleSummaryFormatter,
  CompaniesFormatter,
  DateRenderer,
  GhostLinkRenderer,
} from '../../../components/formatters';
import { ContextMenuRenderer } from '../../../components/formatters/ContextMenuRenderer';
import { LockedArticleLabel } from '../../../components/RestrictedArticleAccess/LockedArticle';
import { ArticleTableRow } from '../../../models/ArticleTableRow';
import { CONTEXT_MENU_COLUMN_WIDTH, DATE_COLUMN_WIDTH } from '../../../utils/columnConfigConstants';

export const SEARCH_RESULT_TABLES_PAGINATION = {
  options: [5, 10, 25],
  extendedOptions: [100, 200],
  defaultOption: 10,
  defaultExtendedOption: 100,
};

const columnsConfig: (
  routeToCallback: RouteToCallback,
  shouldAllowInitialAction: InitialActionAllower,
  userPermissions: UserPermissions,
  isSearchResult: boolean,
  page?: number
) => TableViewConfig = (routeToCallback, shouldAllowInitialAction, userPermissions, isSearchResult, page) => ({
  columns: [
    {
      dataKey: 'content',
      cellLinkRenderer: GhostLinkRenderer,
      cellDataRenderer: ArticleTitleSummaryFormatter,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
      cellRendererProps: { page, isSearchResult },
    },
    {
      dataKey: 'companies',
      // TODO: Switch to TickersFormatter when Ticker data is fixed
      cellDataRenderer: CompaniesFormatter({ isBold: true }),
      cellClassname: 'cell-company',
      dataViewItemConfiguration: { stopMouseUpPropagation: true, shouldAllowInitialAction, routeToCallback },
    },
    {
      dataKey: 'publishDate',
      cellDataRenderer: DateRenderer,
      width: DATE_COLUMN_WIDTH,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    {
      dataKey: 'id',
      cellDataRenderer: ContextMenuRenderer,
      width: CONTEXT_MENU_COLUMN_WIDTH,
      stopMouseUpPropagation: true,
    },
  ],
  disabled: {
    dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    dataRendererProps: { userPermissions },
  },
});

export const searchResultsTableConfig = (
  onArticleClick: (article: ArticleTableRow, event: MouseEvent<HTMLElement>) => void,
  userPermissions: UserPermissions,
  isSearchResult: boolean,
  page?: number
): DataViewConfig<ArticleTableRow> => {
  const routeToCallback: RouteToCallback = (articleId) => ROUTES.article(articleId);
  const shouldAllowInitialAction: InitialActionAllower = (row) => !!row?.isDisabled;

  return {
    tableViewConfig: columnsConfig(routeToCallback, shouldAllowInitialAction, userPermissions, isSearchResult, page),
    onRowMouseUp: onArticleClick,
    getIsDisabled: (article: ArticleTableRow) => !article.canAccess,
    disabledItemElement: LockedArticleLabel,
  };
};
